// The firm cockpit's global focus-ratio cell: one client's reading of whichever ratio the firm has
// focused the roster on — a 12-month sparkline, the end value, the change from the prior month, and
// the benchmark when the ratio has one.
//
// Benchmark treatment is deliberately quiet. A roster row already carries a name, a grade, a stage
// and two other numbers, so the benchmark gets exactly two marks: a faint dashed reference line in
// the sparkline and a muted "vs <value>" under the reading. The source (IRS / own average / band)
// lives in the tooltip rather than a third visible chip.

import React from 'react'
import type { FocusRatioSlice } from '../../../data/ServerApiClient'
import { formatRatioValue } from './cfoFormat'
import {
  ratioBenchmarkAnchor,
  ratioBenchmarkSource,
  ratioNoBenchmarkNote,
} from './cfoRatioBenchmarking'
import { Sparkline } from './charts'

const SPARK_W = 76
const SPARK_H = 26

export type CockpitFocusRatioCellProps = {
  /** The focused ratio for this client, or null when the client's pack has no such entry. */
  slice: FocusRatioSlice | null | undefined
  /** True once this client has been analyzed at all — distinguishes "no pack yet" from "not tracked". */
  analyzed: boolean
}

/**
 * Whether a move in this ratio is an improvement. Polarity, never raw sign: a falling DSO is good
 * news and a falling current ratio is not. `null` polarity (DPO, A/P turnover) has no better
 * direction, so the caller renders those deltas without a good/bad tone.
 */
export function focusDeltaIsGood(
  delta: number,
  higherIsBetter: boolean | null,
): boolean | null {
  if (higherIsBetter == null) return null
  if (delta === 0) return null
  return higherIsBetter ? delta > 0 : delta < 0
}

/** Latest minus the immediately prior month, from the trailing trend. Null without two finite points. */
export function focusMonthOverMonthDelta(slice: FocusRatioSlice): number | null {
  const finite = (slice.trend || [])
    .map((p) => p?.value)
    .filter((v): v is number => v != null && Number.isFinite(v))
  if (finite.length < 2) return null
  return finite[finite.length - 1] - finite[finite.length - 2]
}

/**
 * Tooltip label for one trend point. Carries the year, unlike the Ratios page's `formatShortMonth`:
 * a 12-month window straddles a year boundary, and "Jan" alone is ambiguous there.
 */
export function formatFocusPeriod(period: string | null | undefined): string {
  if (!period) return ''
  const parsed = new Date(`${String(period).slice(0, 10)}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return String(period).slice(0, 7)
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Format a delta in the ratio's own unit — percentage points for fractions, days for days. */
export function formatFocusDelta(delta: number, unit: FocusRatioSlice['unit']): string {
  const abs = Math.abs(delta)
  switch (unit) {
    case 'ratio':
      return `${(abs * 100).toFixed(1)} pts`
    case 'days':
      return `${abs.toFixed(0)}d`
    case 'times':
      return abs.toFixed(2)
    default:
      return formatRatioValue(abs, unit)
  }
}

export function CockpitFocusRatioCell({ slice, analyzed }: CockpitFocusRatioCellProps) {
  if (!slice) {
    return (
      <div className="r2-focus">
        <span className="r2-focus-empty">{analyzed ? 'Not tracked' : 'Not analyzed yet'}</span>
      </div>
    )
  }

  // Keep points and their period labels aligned — filtering values alone would shift every label
  // by however many non-finite months the series happens to contain.
  const points = (slice.trend || []).filter(
    (p) => p?.value != null && Number.isFinite(p.value),
  )
  const finite = points.map((p) => p.value)
  const value = slice.latest ?? (finite.length ? finite[finite.length - 1] : null)
  const delta = focusMonthOverMonthDelta(slice)
  const good = delta != null ? focusDeltaIsGood(delta, slice.higher_is_better) : null

  const anchor = ratioBenchmarkAnchor(slice)
  const source = ratioBenchmarkSource(slice)
  const title =
    anchor != null && source
      ? `${slice.label} · benchmark ${formatRatioValue(anchor, slice.unit)} (${source.tag})`
      : `${slice.label} · no benchmark — ${ratioNoBenchmarkNote(slice)}`

  // Shares the roster-wide pill so the legend at the top of the cockpit explains all three trend
  // columns at once.
  const deltaClass = `delta-pill ${good == null ? 'neutral' : good ? 'up' : 'down'}`

  return (
    <div className="r2-focus">
      {finite.length >= 2 ? (
        <Sparkline
          className="r2-focus-spark"
          values={finite}
          // Tone the line by polarity of the month's move, not by window slope, so it agrees
          // with the arrow sitting next to it.
          down={good === false}
          neutral={good == null}
          benchmark={anchor}
          endDot
          interactive
          labels={points.map((p) => formatFocusPeriod(p.period))}
          formatValue={(v) => formatRatioValue(v, slice.unit)}
          width={SPARK_W}
          height={SPARK_H}
          ariaLabel={`${slice.label} trend, ${finite.length} months`}
        />
      ) : (
        <span className="r2-focus-spark-gap" aria-hidden="true" />
      )}
      {/* The benchmark title lives on the reading, not the whole cell: over the sparkline it would
          race the hover tooltip and the browser's native bubble would win. */}
      <div className="r2-focus-read" title={title}>
        <div className="r2-focus-top">
          <span className="r2-focus-val">{formatRatioValue(value, slice.unit)}</span>
          {delta != null && delta !== 0 && (
            <span className={deltaClass}>
              {delta > 0 ? '▲' : '▼'} {formatFocusDelta(delta, slice.unit)}
            </span>
          )}
        </div>
        {anchor != null ? (
          <span className="r2-focus-vs">vs {formatRatioValue(anchor, slice.unit)}</span>
        ) : finite.length < 2 ? (
          <span className="r2-focus-vs">Needs 2+ months</span>
        ) : null}
      </div>
    </div>
  )
}

export default CockpitFocusRatioCell
